/**
 * PricingFeatureMatrix — customer-facing comparison table at the bottom
 * of `/pricing`. Shows every feature × which primary plans include it.
 *
 * UX choice: collapsed by default behind a "Ver comparativa completa"
 * toggle so the pricing page stays scannable. Users that want detail
 * expand it on demand. Mobile-friendly via overflow-x scroll.
 *
 * i18n: supports `es` (default), `en` and `pt`. The `lang` prop is the
 * canonical source — the public /pricing page passes it explicitly so
 * unauthenticated visitors can still switch via the landing language
 * picker. Falls back to LangContext for in-app embeds.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { CheckCircle2, Grid3x3, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";

// ── Feature label dictionary ────────────────────────────────────────
// Co-located with the component so adding a new feature only requires
// editing this file (instead of crossing into the global i18n.js dict
// that lives much further away). Three supported languages mirror the
// rest of the app (es/en/pt). Unknown languages fall back to es.
const FEATURE_LABELS = {
  recommendations:        { es: "Recomendaciones heurísticas", en: "Heuristic recommendations", pt: "Recomendações heurísticas" },
  ai_recommendations:     { es: "Recomendaciones IA",          en: "AI recommendations",        pt: "Recomendações IA" },
  advisor_chat:           { es: "Asesor AI (chat)",            en: "AI Advisor (chat)",          pt: "Consultor IA (chat)" },
  whatsapp_bot:           { es: "WhatsApp Bot",                en: "WhatsApp Bot",               pt: "Bot WhatsApp" },
  telegram_bot:           { es: "Telegram Bot",                en: "Telegram Bot",               pt: "Bot Telegram" },
  weekly_digest:          { es: "Digest semanal",              en: "Weekly digest",              pt: "Resumo semanal" },
  fx_alerts:              { es: "Alertas FX",                  en: "FX alerts",                  pt: "Alertas FX" },
  pdf_reports:            { es: "Reportes PDF",                en: "PDF reports",                pt: "Relatórios PDF" },
  recurring_movements:    { es: "Movimientos recurrentes",     en: "Recurring movements",        pt: "Movimentos recorrentes" },
  basic_reports:          { es: "Reportes básicos",            en: "Basic reports",              pt: "Relatórios básicos" },
  advanced_reports:       { es: "Reportes avanzados",          en: "Advanced reports",           pt: "Relatórios avançados" },
  cash_flow_weekly:       { es: "Flujo de caja semanal",       en: "Weekly cash flow",           pt: "Fluxo de caixa semanal" },
  scenarios:              { es: "Escenarios what-if",          en: "What-if scenarios",          pt: "Cenários what-if" },
  calendar_heatmap:       { es: "Heatmap anual",               en: "Yearly heatmap",             pt: "Mapa de calor anual" },
  amortization:           { es: "Amortización de deudas",      en: "Debt amortisation",          pt: "Amortização de dívidas" },
  smart_alerts:           { es: "Alertas inteligentes",        en: "Smart alerts",               pt: "Alertas inteligentes" },
  roles_permissions:      { es: "Roles y permisos",            en: "Roles & permissions",        pt: "Funções e permissões" },
  accountant_readonly:    { es: "Acceso contador (read-only)", en: "Accountant read-only",       pt: "Acesso contador (somente leitura)" },
  advanced_exports:       { es: "Exportaciones avanzadas",     en: "Advanced exports",           pt: "Exportações avançadas" },
  basic_api:              { es: "API básica",                  en: "Basic API",                  pt: "API básica" },
  audit_log:              { es: "Audit log",                   en: "Audit log",                  pt: "Log de auditoria" },
  whatsapp_record_movements: { es: "Registro de movimientos por WhatsApp", en: "Log movements via WhatsApp", pt: "Registro de movimentos por WhatsApp" },
  whatsapp_query_cash:    { es: "Consulta de caja por WhatsApp", en: "Cash query via WhatsApp",   pt: "Consulta de caixa por WhatsApp" },
  whatsapp_alerts:        { es: "Alertas WhatsApp",             en: "WhatsApp alerts",            pt: "Alertas WhatsApp" },
  // iter-169t — Comprobantes IA (OCR de recibos vía Claude Sonnet)
  image_extractions:      { es: "Recibos IA",                  en: "AI Receipts",                pt: "Recibos IA" },
};

// UI shell strings (toggle button, table headers, footer note). Kept
// alongside FEATURE_LABELS for the same reason — one place to update.
const UI_STRINGS = {
  title:        { es: "Comparativa completa de funciones", en: "Full feature comparison", pt: "Comparativo completo de funções" },
  feature_col:  { es: "Función",                            en: "Feature",                  pt: "Função" },
  show_table:   { es: "Ver tabla",                          en: "Show table",               pt: "Ver tabela" },
  addons_h:     { es: "Add-ons disponibles",                en: "Available add-ons",         pt: "Add-ons disponíveis" },
  current_plan: { es: "Tu plan",                            en: "Your plan",                 pt: "Seu plano" },
  available_from_free: {
    es: "Disponible para todos los planes (incluye Free).",
    en: "Available for every plan (Free included).",
    pt: "Disponível para todos os planos (Free incluído).",
  },
  requires_plan: {
    es: (names) => `Requiere ${names}.`,
    en: (names) => `Requires ${names}.`,
    pt: (names) => `Requer ${names}.`,
  },
  catalog_for:  {
    es: (country) => `Catálogo para ${country}. Cambia tu país en /perfil si quieres ver otro.`,
    en: (country) => `Catalog for ${country}. Change your country in /profile to see another.`,
    pt: (country) => `Catálogo para ${country}. Altere seu país em /perfil para ver outro.`,
  },
};

function pickLang(lang) {
  return lang === "en" || lang === "pt" ? lang : "es";
}

function labelFor(key, lang) {
  const e = FEATURE_LABELS[key];
  if (!e) return key;
  return e[pickLang(lang)] || e.es;
}

function uiStr(key, lang, ...args) {
  const entry = UI_STRINGS[key];
  if (!entry) return key;
  const value = entry[pickLang(lang)] ?? entry.es;
  return typeof value === "function" ? value(...args) : value;
}

export function PricingFeatureMatrix({ lang: langProp }) {
  const [open, setOpen] = useState(false);
  // Prop wins (public /pricing passes it explicitly) but fall back to
  // the in-app context for embeds where the prop is omitted.
  const ctx = useLang();
  const lang = pickLang(langProp || ctx?.lang || "es");
  const { data } = useQuery({
    queryKey: ["pricing-features-matrix"],
    queryFn: async () => (await api.get("/billing/features/public-matrix")).data,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const matrix = data?.matrix || [];
  const primaries = data?.primaries || [];
  const secondaries = data?.secondaries || [];
  const userLineage = data?.user_primary_lineage || null;
  if (matrix.length === 0 && primaries.length === 0) return null;

  // Map primary id → name + lineage for the addons subtable.
  const primariesById = new Map(primaries.map((p) => [p.id, p]));
  const lineageToName = new Map(primaries.map((p) => [p.lineage_id, p.name]));

  return (
    <Card
      className="p-5 rounded-2xl border-slate-200"
      data-testid="pricing-features-matrix"
    >
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="pricing-features-matrix-toggle"
        className="w-full flex items-center justify-between gap-2 group"
      >
        <div className="flex items-center gap-2">
          <Grid3x3 className="h-4 w-4 text-slate-600" />
          <span className="font-semibold text-slate-900">
            {uiStr("title", lang)}
          </span>
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-slate-500 group-hover:text-slate-700" />
          : <ChevronDown className="h-4 w-4 text-slate-500 group-hover:text-slate-700" />}
      </button>

      {open && (
        <div className="mt-4 overflow-x-auto" data-testid="pricing-features-matrix-body">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b-2 border-slate-200">
              <tr>
                <th className="text-left py-2 pr-3 font-medium">{uiStr("feature_col", lang)}</th>
                {primaries.map((p) => {
                  const isCurrent = !!userLineage && p.lineage_id === userLineage;
                  return (
                    <th
                      key={p.id}
                      data-testid={`pricing-matrix-header-${p.id}`}
                      data-current={isCurrent ? "true" : "false"}
                      className={`text-center py-2 px-3 font-semibold ${
                        isCurrent
                          ? "text-emerald-700 bg-emerald-50/60 rounded-t-md"
                          : p.is_free
                            ? "text-slate-500"
                            : "text-slate-900"
                      }`}
                    >
                      {p.name}
                      {isCurrent && (
                        <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 mt-0.5">
                          {uiStr("current_plan", lang)}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => {
                const included = new Set(row.primary_plan_ids);
                return (
                  <tr
                    key={row.feature_key}
                    data-testid={`pricing-matrix-row-${row.feature_key}`}
                    className="border-b border-slate-100 hover:bg-slate-50/40"
                  >
                    <td className="py-2.5 pr-3 text-slate-900">{labelFor(row.feature_key, lang)}</td>
                    {primaries.map((p) => {
                      const isCurrent = !!userLineage && p.lineage_id === userLineage;
                      const has = included.has(p.id); // Free (id="free") never matches → always —
                      return (
                        <td
                          key={p.id}
                          className={`py-2.5 px-3 text-center ${isCurrent ? "bg-emerald-50/40" : ""}`}
                        >
                          {has ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── Addons subtable ───────────────────────────────────── */}
          {secondaries.length > 0 && (
            <div className="mt-6" data-testid="pricing-features-matrix-addons">
              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" />
                {uiStr("addons_h", lang)}
              </h3>
              <table className="w-full text-sm border-t border-slate-200">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left py-2 pr-3 font-medium">{uiStr("feature_col", lang)}</th>
                    <th className="text-left py-2 px-3 font-medium">Add-on</th>
                    <th className="text-left py-2 px-3 font-medium">{lang === "en" ? "Compatibility" : lang === "pt" ? "Compatibilidade" : "Compatibilidad"}</th>
                  </tr>
                </thead>
                <tbody>
                  {secondaries.map((s) => {
                    const allowed = s.visible_in_primary_plans || [];
                    const isUniversal = allowed.length === 0;
                    const allowedNames = allowed
                      .map((lineage) => lineageToName.get(lineage))
                      .filter(Boolean);
                    const compatLine = isUniversal
                      ? uiStr("available_from_free", lang)
                      : uiStr("requires_plan", lang, allowedNames.join(" / ") || "—");
                    // Find which features this addon unlocks (from matrix rows).
                    const unlocks = matrix
                      .filter((row) => row.secondary_plan_ids?.includes(s.id))
                      .map((row) => labelFor(row.feature_key, lang));
                    return (
                      <tr
                        key={s.id}
                        data-testid={`pricing-matrix-addon-${s.id}`}
                        className="border-t border-slate-100 hover:bg-slate-50/40 align-top"
                      >
                        <td className="py-2.5 pr-3 text-slate-900">
                          {unlocks.length > 0 ? (
                            <ul className="space-y-0.5">
                              {unlocks.map((u, i) => (
                                <li key={i} className="flex items-start gap-1.5">
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" />
                                  <span>{u}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-slate-400 italic text-xs">
                              {lang === "en" ? "(see plan)" : lang === "pt" ? "(ver plano)" : "(ver plan)"}
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 px-3 font-medium text-slate-900">{s.name}</td>
                        <td className="py-2.5 px-3 text-[11px] text-slate-600">{compatLine}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {data?.country && (
            <p className="text-[11px] text-slate-400 mt-3 italic">
              {uiStr("catalog_for", lang, data.country)}
            </p>
          )}
        </div>
      )}

      {!open && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="mt-3"
          data-testid="pricing-features-matrix-expand"
        >
          {uiStr("show_table", lang)}
        </Button>
      )}
    </Card>
  );
}
